import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Repeat, Search, FileSpreadsheet } from "lucide-react";

const COMPANY_LABELS = { misas: "Misaş", mis_holding: "Mis Holding", koop: "Mis Koop." };
const COMPANY_CLS = { misas: "company-misas", mis_holding: "company-mh", koop: "company-koop" };

const FILTERS = [
  { key: "all",         label: "Tümü" },
  { key: "misas",       label: "Misaş" },
  { key: "mis_holding", label: "Mis Holding" },
  { key: "koop",        label: "Mis Koop." },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch (_) { return iso; }
}

export default function Devirs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/devirs`, { withCredentials: true });
        setItems(r.data || []);
      } catch (_) { toast.error("Devir listesi yüklenemedi"); }
      finally { setLoading(false); }
    })();
  }, []);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/devirs/export.xlsx`, { responseType: "blob", withCredentials: true });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : "hisse-devirleri.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Excel dosyası indirildi");
    } catch (_) { toast.error("Excel indirilemedi"); }
    finally { setExporting(false); }
  };

  const query = q.trim().toLocaleLowerCase("tr-TR");
  const visible = useMemo(() => items.filter(d => {
    if (filter !== "all" && d.company !== filter) return false;
    if (!query) return true;
    return (
      (d.transferor_ad_soyad || "").toLocaleLowerCase("tr-TR").includes(query) ||
      (d.transferee_ad_soyad || "").toLocaleLowerCase("tr-TR").includes(query) ||
      (d.yk_karar_no || "").toLocaleLowerCase("tr-TR").includes(query)
    );
  }), [items, filter, query]);

  const totals = useMemo(() => {
    const by = { misas: 0, mis_holding: 0, koop: 0 };
    for (const d of items) if (by[d.company] !== undefined) by[d.company] += d.hisse;
    return by;
  }, [items]);

  const donors = useMemo(() => {
    const map = new Map();
    for (const d of items) {
      const key = d.transferor_id;
      const cur = map.get(key) || { id: key, name: d.transferor_ad_soyad, total: 0, count: 0 };
      cur.total += d.hisse; cur.count += 1;
      map.set(key, cur);
    }
    return [...map.values()].sort((a, b) => b.total - a.total).slice(0, 5);
  }, [items]);

  return (
    <div className="space-y-6" data-testid="devirs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Hisse Devirleri</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Hisse Devri Yapanlar</h1>
          <p className="text-slate-600 mt-2 text-sm">Sistem üzerinden gerçekleştirilen bütün hisse devir işlemlerinin özeti — kim, hangi şirkette, kime, kaç hisse devretti.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="devirs-export"
            onClick={exportXlsx}
            disabled={exporting || items.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <FileSpreadsheet size={15}/> {exporting ? "Hazırlanıyor..." : "Excel'e Aktar"}
          </button>
          <div className="text-sm text-slate-500 font-mono-nums">Toplam işlem: {items.length}</div>
        </div>
      </div>

      {/* Company totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(COMPANY_LABELS).map(([key, label]) => (
          <div key={key} className="crisp-card p-5">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${COMPANY_CLS[key]}`}>{label}</span>
            <div className="mt-3 flex items-baseline gap-2">
              <div className="text-3xl font-extrabold text-slate-900 font-mono-nums">{totals[key] || 0}</div>
              <div className="text-slate-500 text-xs">devredilen hisse</div>
            </div>
          </div>
        ))}
      </div>

      {/* Top donors */}
      {donors.length > 0 && (
        <div className="crisp-card p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-3">En Çok Hisse Devreden Ortaklar</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {donors.map(d => (
              <Link key={d.id} to={`/ortak/${d.id}`} data-testid={`devirs-top-${d.id}`}
                    className="rounded-md border border-slate-200 p-3 hover:bg-slate-50">
                <div className="text-sm font-semibold text-slate-900 truncate">{d.name || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-1">{d.count} işlem</div>
                <div className="text-xl font-extrabold text-slate-900 font-mono-nums mt-1">{d.total} <span className="text-xs text-slate-500 font-normal">hisse</span></div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(f => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
            data-testid={`devirs-filter-${f.key}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="devirs-search" value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Devir eden / alan / YK ara..."
                 className="pl-9 pr-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white w-72" />
        </div>
      </div>

      {/* Table */}
      <div className="crisp-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Yükleniyor...</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Repeat size={26} className="mx-auto mb-2 text-slate-400"/>
            <div className="text-sm">Henüz kayıtlı bir hisse devri bulunmuyor.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Tarih</th>
                  <th className="text-left px-4 py-3">Şirket</th>
                  <th className="text-left px-4 py-3">Devir Eden</th>
                  <th className="text-center px-4 py-3"></th>
                  <th className="text-left px-4 py-3">Devir Alan</th>
                  <th className="text-right px-4 py-3">Hisse</th>
                  <th className="text-left px-4 py-3">YK Karar No</th>
                  <th className="text-left px-4 py-3">İşlemi Yapan</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(d => (
                  <tr key={d.id} data-testid={`devirs-row-${d.devir_pair_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono-nums text-slate-700 whitespace-nowrap">{fmtDate(d.changed_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${COMPANY_CLS[d.company] || ""}`}>
                        {COMPANY_LABELS[d.company] || d.company}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/ortak/${d.transferor_id}`} className="font-semibold text-slate-900 hover:text-slate-700">
                        {d.transferor_ad_soyad || "—"}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-400"><ArrowRight size={14} className="inline"/></td>
                    <td className="px-4 py-3">
                      <Link to={`/ortak/${d.transferee_id}`} className="font-semibold text-slate-900 hover:text-slate-700">
                        {d.transferee_ad_soyad || "—"}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right font-mono-nums font-bold text-slate-900">{d.hisse}</td>
                    <td className="px-4 py-3 font-mono-nums text-slate-700">{d.yk_karar_no || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-slate-600 text-[12px]">{d.changed_by_name || d.changed_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
